// Google Analytics 4 — loads ONLY after the visitor accepts on the cookie banner (see CookieConsent.tsx),
// and only on the live site. Until then, and for visitors who decline, trackEvent does nothing.

export type AnalyticsEvent =
  | 'page_view' |'project_view' |'project_gallery_open' |'knowledge_article_view' |'start_project_click' |'form_start' |'form_submit' |'whatsapp_click' |'phone_click' |'email_click' |'CTA_click'
  // Apartment showcase pages (/apartment-interiors/…)
  | 'showcase_view' | 'gate_shown' | 'wa_click' | 'email_submit' | 'unlock' | 'walkthrough_open'
  | 'share_click' | 'shared_link_open' | 'quote_request' | 'studio_booking' | 'video_call_booking' | 'slider_use'
  | 'feedback_yes' | 'feedback_no' | 'feedback_missing';

export interface EventProperties {
  page?: string;
  project_slug?: string;
  article_slug?: string;
  cta_label?: string;
  form_name?: string;
  [key: string]: string | number | boolean | undefined;
}

// Public ID (it is visible in every visitor's browser anyway). Netlify can override it with
// NEXT_PUBLIC_GA_MEASUREMENT_ID; the template placeholder in .env is ignored.
const ENV_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? '';
export const GA_ID = /^G-[A-Z0-9]{6,}$/.test(ENV_ID) ? ENV_ID : 'G-4VJJ2WXSWR';

/** Analytics runs on the production build only — never while developing locally */
export const analyticsEnabled = process.env.NODE_ENV === 'production';

export const CONSENT_KEY = 'willow-cookie-consent'; // 'granted' | 'denied'

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Loads GA4 (called once the visitor has accepted) */
export function loadAnalytics(): void {
  if (!analyticsEnabled || typeof window === 'undefined' || window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { anonymize_ip: true });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

export function trackEvent(
  event: AnalyticsEvent,
  properties?: EventProperties
): void {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics]', event, properties || {});
  }
  // GA4 records page views itself (including in-site navigation), so page_view is not sent twice
  if (event === 'page_view' || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', event, properties || {});
}
